import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { updateTableAssignments, swapPlayers } from '@/lib/api';
import type { Round } from '@swiss-magic/shared-types';

type Input = {
  tournamentId: string | undefined;
  rounds: Round[];
  setRounds: Dispatch<SetStateAction<Round[]>>;
  selectedRound: number;
  handleActionError: (err: unknown, msg: string) => void;
};

export function useTableReorderAndSwap({
  tournamentId,
  rounds,
  setRounds,
  selectedRound,
  handleActionError,
}: Input) {
  const { t } = useTranslation('director');
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [swapSelection, setSwapSelection] = useState<string | null>(null);

  // Clear per-round UI state when the user switches rounds (or tournaments):
  // a swap-selection targets one round, and a stale dragIndex would grey out a
  // row at the same position in the newly selected round.
  useEffect(() => {
    setSwapSelection(null);
    setDragIndex(null);
  }, [tournamentId, selectedRound]);

  function handleDragStart(index: number) {
    setDragIndex(index);
  }

  function handleDragEnd() {
    setDragIndex(null);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
  }

  async function handleDrop(targetIndex: number) {
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      return;
    }

    const round = rounds[selectedRound];
    if (!round || !tournamentId) return;

    const pairings = [...round.pairings];
    const draggedPairing = pairings[dragIndex];
    const targetPairing = pairings[targetIndex];

    const dragTable = draggedPairing.tableNumber;
    const targetTable = targetPairing.tableNumber;

    const assignments = [
      { pairingId: draggedPairing.id, tableNumber: targetTable },
      { pairingId: targetPairing.id, tableNumber: dragTable },
    ];

    try {
      const updated = await updateTableAssignments(
        tournamentId,
        round.id,
        assignments,
      );
      setRounds((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r)),
      );
    } catch (err) {
      handleActionError(err, t('rounds_page.toast.update_table_failed'));
    }

    setDragIndex(null);
  }

  async function handleSwap(sourceIndex: number, targetIndex: number) {
    const round = rounds[selectedRound];
    if (!round || !tournamentId) return;

    const pairings = [...round.pairings];
    const sourcePairing = pairings[sourceIndex];
    const targetPairing = pairings[targetIndex];

    const assignments = [
      { pairingId: sourcePairing.id, tableNumber: targetPairing.tableNumber },
      { pairingId: targetPairing.id, tableNumber: sourcePairing.tableNumber },
    ];

    try {
      const updated = await updateTableAssignments(
        tournamentId,
        round.id,
        assignments,
      );
      setRounds((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r)),
      );
    } catch (err) {
      handleActionError(err, t('rounds_page.toast.update_table_failed'));
    }
  }

  async function handleSwapPlayers(playerIdA: string, playerIdB: string) {
    const round = rounds[selectedRound];
    if (!round || !tournamentId) return;
    try {
      const updated = await swapPlayers(tournamentId, round.id, playerIdA, playerIdB);
      setRounds((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r)),
      );
    } catch (err) {
      handleActionError(err, t('rounds_page.toast.swap_players_failed'));
    } finally {
      setSwapSelection(null);
    }
  }

  function handlePlayerSelect(playerId: string) {
    if (swapSelection === null) {
      setSwapSelection(playerId);
    } else if (swapSelection === playerId) {
      setSwapSelection(null);
    } else {
      handleSwapPlayers(swapSelection, playerId);
    }
  }

  return {
    dragIndex,
    swapSelection,
    setSwapSelection,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDrop,
    handleSwap,
    handleSwapPlayers,
    handlePlayerSelect,
  };
}
